import * as React from 'react';
import { View, ActivityIndicator } from 'react-native';
import { useLocation } from 'react-router';
import { loadCheckoutWebComponents } from '@checkout.com/checkout-web-components';
import color from '../../style/color';


function decodePaymentSessionToken(token: string): Record<string, unknown> {
    const colonIndex = token.indexOf(':');
    const base64Data = colonIndex !== -1 ? token.substring(colonIndex + 1) : token;
    const decoded = JSON.parse(atob(base64Data));
    return { id: decoded.id, payment_session_token: token, payment_session_secret: decoded.payment_session_secret };
}

/**
 * Intermediate page that checkout.com redirects to after 3DS (instead of the final success page).
 * Re-initializes the checkout.com Flow SDK so onPaymentCompleted fires with the real payment result,
 * then redirects to the actual success or failure page.
 * The paymentSessionToken is injected into the URL by the extension background script.
 */
export default function GiftCardPaymentVerifyPage() {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const paymentSessionToken = params.get('paymentSessionToken');
    const paymentSessionId = params.get('paymentSessionId') ?? '';
    const platformIssuerId = params.get('platformIssuerId') ?? '';
    const numberOfCards = params.get('numberOfCards') ?? '1';

    const successBase = `/gift-card-payment/success?paymentSessionId=${encodeURIComponent(paymentSessionId)}&platformIssuerId=${encodeURIComponent(platformIssuerId)}&numberOfCards=${numberOfCards}`;
    const failureUrl = `/gift-card-payment/failure?paymentSessionId=${encodeURIComponent(paymentSessionId)}&platformIssuerId=${encodeURIComponent(platformIssuerId)}`;

    React.useEffect(() => {
        if (!paymentSessionToken) return; // Wait for background script to inject token

        // If cko-payment-id is already in the URL, checkout.com has confirmed the payment
        // completed (it only redirects to successUrl for completed payments). Redirect to
        // success immediately — no need to re-initialize the SDK.
        // The delivery mutation verifies actual authorization server-side.
        const ckoPaymentId = new URLSearchParams(window.location.search).get('cko-payment-id');
        if (ckoPaymentId) {
            window.location.href = `${successBase}&cko-payment-id=${encodeURIComponent(ckoPaymentId)}`;
            return;
        }

        // No cko-payment-id: this is a 3DS card payment redirect. Re-initialize the SDK so
        // onPaymentCompleted fires with the real authorization result.
        const isSandbox = window.location.hostname === 'localhost' || window.location.hostname.includes('dev.');
        const publicKey = isSandbox
            ? (process.env.REACT_APP_CKO_DEV_PUBLIC_KEY ?? 'pk_sbox_slcj5tjfxyr6rph45u6cmnaghih')
            : (process.env.REACT_APP_CKO_PROD_PUBLIC_KEY ?? 'pk_qnwnjsjn43im2xyh52uzbgcjam6');

        let isMounted = true;
        let mountedComponent: any = null;
        const timeoutId = setTimeout(() => {
            if (isMounted) window.location.href = failureUrl;
        }, 15000);

        loadCheckoutWebComponents({
            paymentSession: decodePaymentSessionToken(paymentSessionToken),
            publicKey,
            environment: isSandbox ? 'sandbox' : 'production',
            onPaymentCompleted: (_component: any, paymentResponse: any) => {
                clearTimeout(timeoutId);
                if (!isMounted) return;
                if (!paymentResponse?.approved) {
                    window.location.href = failureUrl;
                    return;
                }
                const paymentId = paymentResponse?.id;
                const redirectUrl = paymentId
                    ? `${successBase}&cko-payment-id=${encodeURIComponent(paymentId)}`
                    : successBase;
                window.location.href = redirectUrl;
            },
            onError: (_component: any, error: any) => {
                clearTimeout(timeoutId);
                if (isMounted) window.location.href = failureUrl;
            },
        } as any).then((checkout: any) => {
            if (!isMounted) return;
            const container = document.getElementById('checkout-verify-container');
            if (container) {
                mountedComponent = checkout.create('flow');
                mountedComponent.mount(container);
            }
        }).catch(() => {
            clearTimeout(timeoutId);
            if (isMounted) window.location.href = failureUrl;
        });

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            mountedComponent?.unmount();
        };
    }, [paymentSessionToken, successBase, failureUrl]);

    return (
        <>
            <View style={{ height: '100vh', justifyContent: 'center' }}>
                <ActivityIndicator size="large" color={color.alto} />
            </View>
            <div
                id="checkout-verify-container"
                style={{ position: 'absolute', top: -9999, left: -9999, width: 1, height: 1, overflow: 'hidden' }}
            />
        </>
    );
}
